// modules/auth/AuthContext.tsx

import React, { createContext, useContext, useEffect } from 'react';
import { User, onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';
import { getCurrentTenantId, tenantService } from '../../core/services/firebase/firestore';
import { auth } from '../../core/services/firebase/config';
import { Tenant } from '../../core/types';

// Lista de UIDs de administradores da plataforma
export const ADMIN_UIDS = [
  '1AtcwSyb4rXz5zAUITS3MOnUIgS2', // email.pescar.app@gmail.com
];

interface AuthContextType {
  user: User | null;
  tenant: Tenant | null;
  loading: boolean;
  isAdmin: boolean;
  isTenantMember: boolean;
  signOut: () => Promise<void>;
  checkPermission: (role: string) => boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  tenant: null,
  loading: true,
  isAdmin: false,
  isTenantMember: false,
  signOut: async () => {},
  checkPermission: () => false,
});

export const useAuth = () => useContext(AuthContext);

const MOCK_USER = {
  uid: 'dev-user-123',
  email: 'dev@nossapesca.com',
  displayName: 'Desenvolvedor',
  emailVerified: true,
  isAnonymous: false,
  phoneNumber: null,
  photoURL: null,
  providerId: 'firebase',
  tenantId: null,
  metadata: { creationTime: new Date().toISOString(), lastSignInTime: new Date().toISOString() },
  providerData: [],
  refreshToken: '',
  delete: async () => {},
  getIdToken: async () => 'mock-token',
  getIdTokenResult: async () => ({
      authTime: new Date().toISOString(),
      expirationTime: new Date(Date.now() + 3600000).toISOString(),
      issuedAtTime: new Date().toISOString(),
      signInProvider: 'firebase',
      signInSecondFactor: null,
      token: 'mock-token',
      claims: {}
  }),
  reload: async () => {},
  toJSON: () => ({}),
} as unknown as User;

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = React.useState<User | null>(null);
  const [tenant, setTenant] = React.useState<Tenant | null>(null);
  const [loading, setLoading] = React.useState(true);

  // Determinar Tenant Atual
  useEffect(() => {
    const loadTenant = async () => {
      const tenantId = getCurrentTenantId();
      if (tenantId && tenantId !== 'platform' && tenantId !== 'builder') {
        const tenantData = await tenantService.getTenant(tenantId);
        setTenant(tenantData as Tenant);
      }
    };
    loadTenant();
  }, []);

  // Monitorar Estado de Autenticação
  useEffect(() => {
    // 1. Bypass para Desenvolvimento Local
    const devUserBypass = localStorage.getItem('devMode') === 'true';
    if (devUserBypass) {
        // Simular um usuário logado se estiver no modo dev
        const mockUser = {
            uid: 'dev-admin-123',
            email: 'admin@nossapesca.com',
            displayName: 'Admin (Dev Mode)',
            photoURL: null,
            emailVerified: true
        } as User;
        setUser(mockUser);
        setLoading(false);
        return;
    }

    // 2. Monitorar Estado de Autenticação Real do Firebase
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);
      
      // Aqui poderíamos buscar claims customizadas para roles
      if (firebaseUser) {
        // Ex: const token = await firebaseUser.getIdTokenResult();
        // setRoles(token.claims.roles);
      }
    });

    return () => unsubscribe();
  }, []);

  const signOut = async () => {
    await firebaseSignOut(auth);
  };

  const checkPermission = (role: string) => {
    // Implementar lógica de permissão baseada em claims ou documento do usuário no Firestore
    return true; // Simplificado para demo
  };

  // Verificação de admin baseada em whitelist
  const isAdmin = !!user && ADMIN_UIDS.includes(user.uid);
  const isTenantMember = !!user; // TODO: Verificar se usuário pertence a este tenant

  return (
    <AuthContext.Provider value={{
      user,
      tenant,
      loading,
      isAdmin,
      isTenantMember,
      signOut,
      checkPermission
    }}>
      {children}
    </AuthContext.Provider>
  );
};
